import { Direction } from 'viewpointBuilder/types';
import { logError } from '@ardoq/logging';
import {
  PersistedLoadedStateParams,
  TraversalCreatedInViewLoadedStateParams,
  BooleanOperator,
  CreatedItemsLoadedStateParams,
  DirectedTripleWithFilters,
  HexIndex,
  SearchLoadedStateParams,
  TraversalLoadedStateParams,
  LoadedStateType,
  Operator,
  Path,
  QueryBuilderQuery,
  QueryBuilderRow,
  QueryBuilderRuleValue,
  StartSetExpansion,
  TraversalParams,
  ViewpointBuilderFilters,
  TraversalPathMatchingType,
  PathCollapsingRule,
  LineType,
  ArrowType,
  TripleQualifier,
  QueryBuilderRule,
} from '@ardoq/api-types';
import { defaultKeys } from './defaultKeys';
import { FILTER_ID_PREFIX } from 'viewpointBuilder/getId';
import {
  BASE,
  CLOSE_AND_RULES,
  CLOSE_OR_RULES,
  COLLAPSED_PATH_SEPARATOR,
  FILTER_SEPARATOR,
  ID_SEPARATOR,
  OPEN_AND_RULES,
  OPEN_OR_RULES,
  PATH_SEPARATOR,
  QUERY_RULE_TERMINATOR,
  SEARCH_SEPARATOR,
  STATE_SEPARATOR,
  TOKEN_SEPARATOR,
  TRAVERSAL_SEPARATOR,
} from '../consts';
import { queryBuilderOperations } from '@ardoq/query-builder';
import { ARDOQ_ID_LENGTH } from '@ardoq/common-helpers';

const STATIC_TRIPLE_PROPERTIES_COUNT = 4;
const STATIC_COLLAPSED_PATH_PROPERTIES_COUNT = 7; // excluding the `path`-field

const loadedTypesArray: LoadedStateType[] = [
  LoadedStateType.SEARCH,
  LoadedStateType.TRAVERSAL,
  LoadedStateType.CREATED_ITEMS,
  LoadedStateType.TRAVERSAL_CREATED_IN_VIEW,
];

const decodeType = (type: number) => loadedTypesArray[type];

const pathMatchingTypesArray: TraversalPathMatchingType[] = [
  TraversalPathMatchingType.LOOSE,
  TraversalPathMatchingType.STRICT,
  TraversalPathMatchingType.SUPERSTRICT,
];

const decodePathMatching = (type: number) => pathMatchingTypesArray[type];

const tripleQualifierTypeArray: TripleQualifier[] = [
  'optional',
  'required',
  'excluded',
];
const decodeQualifier = (encodedQualifier: string) => {
  const qualifier = Number(encodedQualifier);
  return {
    qualifier: [1, 2].includes(qualifier) // we only care about 'required' and 'excluded'
      ? tripleQualifierTypeArray[qualifier]
      : undefined,
  };
};

const addFilterPrefix = (filterId?: string) => `${FILTER_ID_PREFIX}${filterId}`;

export const decodeTraversal = (
  type: LoadedStateType,
  encodedTraversal: string,
  hexIndexToToken: Record<string, string | undefined>
): TraversalLoadedStateParams | TraversalCreatedInViewLoadedStateParams => {
  const [
    isHidden,
    traversalId,
    pathMatching,
    encodedStartSet,
    encodedStartQuery,
    encodedPaths,
    encodedFilters,
    encodedPathCollapsingRules,
    encodedStartSetExpansion,
  ] = encodedTraversal.split(TRAVERSAL_SEPARATOR);

  // 0 = no expansion, 1 = descendants. Absent in older encodings -> no expansion.
  const startSetExpansion: StartSetExpansion | undefined =
    encodedStartSetExpansion === '1' ? 'descendants' : undefined;

  const startSet = encodedStartSet ? encodedStartSet.split(ID_SEPARATOR) : [];
  let startQuery;
  try {
    startQuery = encodedStartQuery
      ? queryBuilderOperations.addSubqueryToComponentQuery(
          decodeSearchQuery(encodedStartQuery, hexIndexToToken)
        )
      : undefined;
  } catch (e) {
    logError(Error('Parsing of loadedState.startQuery failed'));
  }
  const tokens = Object.values(hexIndexToToken);

  const directionIndex: Record<string, Direction> = {
    [getPaddedKey(0, tokens)]: 'outgoing',
    [getPaddedKey(1, tokens)]: 'incoming',
  };

  const paths = decodePaths(
    encodedPaths,
    tokens,
    hexIndexToToken,
    directionIndex
  );
  const filters = decodeFilters(encodedFilters, tokens, hexIndexToToken);

  const pathCollapsingRules = decodePathCollapsingRules(
    encodedPathCollapsingRules,
    tokens,
    hexIndexToToken,
    directionIndex
  );

  const data: TraversalParams = {
    pathMatching: decodePathMatching(Number(pathMatching)),
    pathCollapsingRules,
    componentSelection: startQuery ? { startSet, startQuery } : { startSet },
    paths,
    filters,
    ...(startSetExpansion ? { startSetExpansion } : {}),
  };

  if (type === LoadedStateType.TRAVERSAL_CREATED_IN_VIEW) {
    return {
      type: LoadedStateType.TRAVERSAL_CREATED_IN_VIEW,
      isHidden: Boolean(Number(isHidden)),
      data,
    } satisfies TraversalCreatedInViewLoadedStateParams;
  }

  return {
    traversalId,
    isHidden: Boolean(Number(isHidden)),
    type: LoadedStateType.TRAVERSAL,
    data,
  } satisfies TraversalLoadedStateParams;
};

const getTokenIndex = (tokens: string[]) =>
  Object.fromEntries(
    tokens.map((token, index) => [
      getPaddedKey(index, tokens),
      token === 'undefined' ? undefined : token,
    ])
  );

const decodePaths = (
  encodedPaths: string,
  tokens: (string | undefined)[],
  hexIndexToToken: Record<HexIndex, string | undefined>,
  directionIndex: Record<string, Direction>
): Path[] => {
  const encodedPathToHexIndexes = getEncodedPathToHexIndexes(tokens);
  return encodedPaths
    .split(PATH_SEPARATOR)
    .map(path => encodedPathToHexIndexes(path))
    .map(pathAsHexIndexes =>
      pathAsHexIndexes.map(
        ([
          setFilterIdsAsDigit,
          qualifier,
          directionAsHexIndex,
          sourceTypeAsHexIndex,
          referenceTypeAsHexIndex,
          targetTypeAsHexIndex,
          ...filterIds
        ]) => {
          return {
            direction: directionIndex[directionAsHexIndex],
            sourceType: hexIndexToToken[sourceTypeAsHexIndex]!,
            referenceType: hexIndexToToken[referenceTypeAsHexIndex]!,
            targetType: hexIndexToToken[targetTypeAsHexIndex]!,
            ...decodeQualifier(qualifier),
            ...getFilterIdsAsDictionary(
              hexIndexToToken,
              filterIds,
              setFilterIdsAsDigit
            ),
          } satisfies DirectedTripleWithFilters;
        }
      )
    );
};

const decodeFilters = (
  encodedFilters: string,
  tokens: (string | undefined)[],
  hexIndexToToken: Record<HexIndex, string | undefined>
): ViewpointBuilderFilters => {
  if (encodedFilters === '') return {};

  const decodeFilter = getDecodeFilter(tokens);
  return Object.fromEntries(
    encodedFilters.split(FILTER_SEPARATOR).map(encodedFilter => {
      const { filterId, condition, rulesAsHexIndexes } =
        decodeFilter(encodedFilter);
      const rules = rulesAsHexIndexes.map(
        ([
          idAsHexIndex,
          fieldAsHexIndex,
          inputAsHexIndex,
          typeAsHexIndex,
          valueAsHexIndex,
          operatorAsHexIndex,
        ]) => {
          return {
            id: hexIndexToToken[idAsHexIndex]!,
            field: hexIndexToToken[fieldAsHexIndex]!,
            input: hexIndexToToken[inputAsHexIndex]!,
            type: hexIndexToToken[typeAsHexIndex]!,
            value: decodeToFilterValue(
              hexIndexToToken[typeAsHexIndex],
              hexIndexToToken[valueAsHexIndex]
            ) as string | number | boolean | null,
            operator: hexIndexToToken[operatorAsHexIndex] as Operator,
          };
        }
      );
      return [
        addFilterPrefix(hexIndexToToken[filterId]),
        {
          filterId: addFilterPrefix(hexIndexToToken[filterId]),
          condition: hexIndexToToken[condition] as BooleanOperator,
          rules,
        },
      ];
    })
  );
};

const getDecodeFilter = (tokens: (string | undefined)[]) => {
  const tokenLength = getMaxSizeFromTokens(tokens);
  const regExpIdConditionRules = new RegExp(
    `^(.{${tokenLength}})(.{${tokenLength}})(.*)$`
  );
  const regExpRules = new RegExp(`.{${tokenLength * 6}}`, 'g');
  const regExpRuleProps = new RegExp(`.{${tokenLength}}`, 'g');
  const mapper = (
    encodedFilter: string
  ): {
    filterId: string;
    condition: string;
    rulesAsHexIndexes: string[][];
  } => {
    const [, filterId, condition, allRules] = encodedFilter.match(
      regExpIdConditionRules
    )!;
    const rules = allRules.match(regExpRules)!;
    return {
      filterId,
      condition,
      rulesAsHexIndexes: rules.map(rule => rule.match(regExpRuleProps)!),
    };
  };
  return mapper;
};

const decodeToFilterValue = (
  type?: string,
  value?: string
): QueryBuilderRuleValue | undefined => {
  if (!(typeof type === 'string' && typeof value === 'string')) {
    logError(
      Error('Decoding of filter value failed: missing values in token map')
    );
    return value;
  }
  if (value === 'null') return null;
  if (value === 'undefined') return undefined;

  switch (type) {
    case 'number':
      return Number.parseInt(value, 10);
    case 'boolean':
      return value === 'true';

    default:
      return value;
  }
};

const decodePathCollapsingRules = (
  encodedRules: string,
  tokens: (string | undefined)[],
  hexIndexToToken: Record<HexIndex, string | undefined>,
  directionIndex: Record<string, Direction>
): PathCollapsingRule[] => {
  if (!encodedRules) return [];
  const maxSize = getMaxSizeFromTokens(tokens);
  const splitter = new RegExp(`(${'.'.repeat(maxSize)})`, 'g');

  const rules = encodedRules.split(COLLAPSED_PATH_SEPARATOR);
  return rules.map(rule => {
    const referenceTypeId = rule.slice(0, ARDOQ_ID_LENGTH);
    const fields = rule.substring(
      ARDOQ_ID_LENGTH,
      ARDOQ_ID_LENGTH + maxSize * STATIC_COLLAPSED_PATH_PROPERTIES_COUNT
    );
    const path = rule.substring(
      ARDOQ_ID_LENGTH + maxSize * STATIC_COLLAPSED_PATH_PROPERTIES_COUNT
    );

    const ruleFields = fields.match(splitter)!;
    const [
      isActive,
      displayText,
      color,
      line,
      lineBeginning,
      lineEnding,
      encodedSvgStyle,
    ] = ruleFields;

    const svgStyleToken = hexIndexToToken[encodedSvgStyle];
    const svgStyle =
      svgStyleToken && svgStyleToken !== 'null' ? svgStyleToken : null;
    const paths = decodePaths(path, tokens, hexIndexToToken, directionIndex);
    return {
      referenceTypeId,
      path: paths[0], // collapsed paths only ever have one path
      displayText: hexIndexToToken[displayText]!,
      isActive: hexIndexToToken[isActive] === 'true',
      referenceStyle: {
        color: hexIndexToToken[color]!,
        line: hexIndexToToken[line] as LineType,
        lineBeginning: hexIndexToToken[lineBeginning] as ArrowType,
        lineEnding: hexIndexToToken[lineEnding] as ArrowType,
        svgStyle,
      },
    };
  });
};

export const decodeSearch = (
  encodedSearch: string,
  hexIndexToToken: Record<string, string | undefined>
): SearchLoadedStateParams => {
  const [isHidden, componentIds, query] = encodedSearch.split(SEARCH_SEPARATOR);

  const startSet = componentIds ? componentIds.split(ID_SEPARATOR) : [];
  const startQuery = query
    ? queryBuilderOperations.addSubqueryToComponentQuery(
        decodeSearchQuery(query, hexIndexToToken)
      )
    : undefined;

  return {
    type: LoadedStateType.SEARCH,
    isHidden: Boolean(Number(isHidden)),
    data: {
      componentSelection: startQuery ? { startSet, startQuery } : { startSet },
    },
  };
};

export const decodeCreatedItemsLoadedState = (
  encodedSearch: string
): CreatedItemsLoadedStateParams => {
  const [isHidden, componentIds, referenceIds] =
    encodedSearch.split(SEARCH_SEPARATOR);
  return {
    type: LoadedStateType.CREATED_ITEMS,
    isHidden: Boolean(Number(isHidden)),
    data: {
      componentIds: componentIds ? componentIds.split(ID_SEPARATOR) : [],
      referenceIds: referenceIds ? referenceIds.split(ID_SEPARATOR) : [], // avoid array with empty string
    },
  };
};

export const decodeSearchQuery = (
  encodedRules: string,
  hexIndexToToken: Record<string, string | undefined>
): QueryBuilderQuery => {
  const tokens = Object.values(hexIndexToToken);
  const maxSize = getMaxSizeFromTokens(tokens);

  const splitter = new RegExp(`(${'.'.repeat(maxSize)})`, 'g');
  const toKeys = (str: string) => str.match(splitter)!;

  return decodeRules(
    encodedRules,
    hexIndexToToken,
    toKeys
  )[0] as QueryBuilderQuery; // we know that the first rule is the QueryBuilderQuery
};

const decodeRules = (
  encodedRules: string,
  hexIndexToToken: Record<string, string | undefined>,
  toKeys: (str: string) => string[]
): QueryBuilderRow[] => {
  const parsedRules = [];
  let processedEncodedRules = encodedRules;
  while (processedEncodedRules.length > 0) {
    if (
      processedEncodedRules.startsWith(OPEN_AND_RULES) ||
      processedEncodedRules.startsWith(OPEN_OR_RULES)
    ) {
      const openingChar = processedEncodedRules[0];
      const closingChar =
        openingChar === OPEN_AND_RULES ? CLOSE_AND_RULES : CLOSE_OR_RULES;
      const closingIndex = getClosingIndex(
        processedEncodedRules,
        openingChar,
        closingChar
      );
      if (closingIndex < 0) throw Error('Decoding search query rule failed');
      const rules = decodeRules(
        processedEncodedRules.slice(1, closingIndex),
        hexIndexToToken,
        toKeys
      );
      parsedRules.push({
        condition:
          openingChar === OPEN_AND_RULES
            ? BooleanOperator.AND
            : BooleanOperator.OR,
        rules,
      });
      processedEncodedRules = processedEncodedRules.slice(closingIndex + 1);
    } else {
      const closingIndex = processedEncodedRules.indexOf(QUERY_RULE_TERMINATOR);
      const rule = processedEncodedRules.slice(0, closingIndex);
      const keys = toKeys(rule);
      const [
        id,
        type,
        input,
        field,
        operator,
        value,
        targetEntityType,
        referenceTypeName,
      ] = keys.map(key => hexIndexToToken[key]);

      if (
        id !== undefined &&
        type !== undefined &&
        input !== undefined &&
        field !== undefined &&
        operator !== undefined &&
        value !== undefined
      ) {
        parsedRules.push({
          id,
          type,
          input,
          field,
          operator: operator as Operator,
          value: decodeToFilterValue(type, value) ?? null,
          targetEntityType: targetEntityType
            ? (targetEntityType as QueryBuilderRule['targetEntityType'])
            : undefined,
          ...(referenceTypeName !== undefined ? { referenceTypeName } : {}),
        });
      } else {
        throw Error(
          'Decoding of search query rule failed with undefined properties'
        );
      }
      processedEncodedRules = processedEncodedRules.slice(closingIndex + 1);
    }
  }
  return parsedRules;
};

const getClosingIndex = (
  encodedRules: string,
  openingChar: string,
  closingChar: string
) => {
  let count = 0;
  let index = -1;
  for (const char of encodedRules) {
    index++;
    if (char === openingChar) count++;
    if (char === closingChar) count--;
    if (count === 0) return index;
  }
  return -1;
};

const getPaddedKey = (() => {
  const maxSizes: Record<number, number> = {};

  return (n: number, tokens: (string | undefined)[]) => {
    let maxSize = maxSizes[tokens.length - 1];
    if (!maxSize) {
      maxSizes[tokens.length - 1] = getMaxSizeFromTokens(tokens);
      maxSize = maxSizes[tokens.length - 1];
    }
    return n.toString(BASE).padStart(maxSize, '0');
  };
})();

const getMaxSizeFromTokens = (tokens: (string | undefined)[]) =>
  (tokens.length - 1).toString(BASE).length;

const getEncodedPathToHexIndexes = (tokens: (string | undefined)[]) => {
  const maxHexIndexSize = getMaxSizeFromTokens(tokens);
  const splitter = new RegExp(`.{${maxHexIndexSize}}`, 'g');
  const encodedPathsToHexIndexes = (
    encodedPaths: string,
    hexIndexes: string[][] = []
  ): string[][] => {
    if (encodedPaths.length === 0) return hexIndexes;

    const setFilterIdsAsDigit = encodedPaths[0];
    const qualifier = encodedPaths[1];

    const tripleLength =
      maxHexIndexSize *
      (STATIC_TRIPLE_PROPERTIES_COUNT +
        getSetFilterIdsCount(Number.parseInt(setFilterIdsAsDigit, BASE)));
    const tripleAsHexIndexes = encodedPaths
      .slice(2, 2 + tripleLength) // skip setFilterIdsAsDigit and qualifier
      .match(splitter)!;

    return encodedPathsToHexIndexes(encodedPaths.slice(2 + tripleLength), [
      ...hexIndexes,
      [setFilterIdsAsDigit, qualifier, ...tripleAsHexIndexes],
    ]);
  };
  return encodedPathsToHexIndexes;
};

const getFilterIdsAsDictionary = (
  hexIndexToToken: Record<string, string | undefined>,
  filters: string[],
  setFilterIdsAsDigit: string
) => {
  const [first, second, third] = filters;
  switch (setFilterIdsAsDigit) {
    // '000'.
    case '0':
      return {};
    // '001'.
    case '1':
      return { referenceFilter: addFilterPrefix(hexIndexToToken[first]) };
    // '010'.
    case '2':
      return { targetFilter: addFilterPrefix(hexIndexToToken[first]) };
    // '011'.
    case '3':
      return {
        targetFilter: addFilterPrefix(hexIndexToToken[first]),
        referenceFilter: addFilterPrefix(hexIndexToToken[second]),
      };
    // '100'.
    case '4':
      return { sourceFilter: addFilterPrefix(hexIndexToToken[first]) };
    // '101'.
    case '5':
      return {
        sourceFilter: addFilterPrefix(hexIndexToToken[first]),
        referenceFilter: addFilterPrefix(hexIndexToToken[second]),
      };
    // '110'.
    case '6':
      return {
        sourceFilter: addFilterPrefix(hexIndexToToken[first]),
        targetFilter: addFilterPrefix(hexIndexToToken[second]),
      };
    // '111'.
    case '7':
      return {
        sourceFilter: addFilterPrefix(hexIndexToToken[first]),
        targetFilter: addFilterPrefix(hexIndexToToken[second]),
        referenceFilter: addFilterPrefix(hexIndexToToken[third]),
      };

    default:
      logError(
        Error('Decoding of filter ids failed with invalid setFilterIdsAsDigit')
      );
      return {};
  }
};

const getSetFilterIdsCount = (setFilterIdsAsDigit: number): number =>
  (setFilterIdsAsDigit & 1) +
  ((setFilterIdsAsDigit >> 1) & 1) +
  ((setFilterIdsAsDigit >> 2) & 1);

export const getTokenIndexFromEncodedTokens = (encodedTokens: string) => {
  const decodedTokens = encodedTokens
    .split(TOKEN_SEPARATOR)
    .map(token => decodeURIComponent(token));

  const tokens = [...defaultKeys, ...decodedTokens];

  return getTokenIndex(tokens);
};

export const decodeLoadedStateV10 = (
  encodedLoadedState: string
): PersistedLoadedStateParams[] => {
  if (encodedLoadedState === '') return [];

  try {
    const parts = encodedLoadedState.split(STATE_SEPARATOR);
    const encodedTokens = parts.pop()!;
    const hexIndexToToken = getTokenIndexFromEncodedTokens(encodedTokens);

    return parts.map(state => {
      const type = decodeType(Number.parseInt(state[0], 10));
      const encodedState = state.slice(1);

      if (type === LoadedStateType.SEARCH)
        return decodeSearch(encodedState, hexIndexToToken);

      if (type === LoadedStateType.CREATED_ITEMS)
        return decodeCreatedItemsLoadedState(encodedState);

      if (
        type === LoadedStateType.TRAVERSAL ||
        type === LoadedStateType.TRAVERSAL_CREATED_IN_VIEW
      )
        return decodeTraversal(type, encodedState, hexIndexToToken);

      throw Error(`Can't decode loaded state with type ${type}`);
    });
  } catch (e) {
    logError(Error('Decoding loaded state failed'));
    return [];
  }
};
